/* src/pages/Billing.jsx
 * Billing and subscription management page
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { db } from '../firebase';
import { getSubmissionLimitForTier, resolveEffectiveTier, LEAD_OVERAGE_USD_BY_TIER } from '../utils/tiers';
import { doc, getDoc } from 'firebase/firestore';
import { useSearchParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import InlineConfirm from '../components/InlineConfirm';
import { BillingPlanSkeleton } from '../components/Skeleton';
import { walletApi, usageApi, smsApi, functionsUrl, getAuthHeaders } from '../utils/api';
import { isAdmin } from '../utils/admin';
import {
  deriveBillingState,
  planButtonLabel as billingPlanButtonLabel,
  isPlanButtonDisabled,
  cancelControlProps,
  statusBannerFlags,
  canceledEndedBannerText,
  BILLING_STATES,
} from '../utils/billingState';
import {
  AUTO_RECHARGE_CONSENT_TEXT,
  AUTO_RECHARGE_TOGGLE_LABEL,
  formatAutoRechargeAuthorizedLine,
} from '../utils/walletAutoRecharge';

const LEDGER_TYPE_LABELS = {
  topup: 'Top-up',
  charge: 'Charge',
  refund: 'Refund',
  release: 'Release',
};

function formatLedgerAmount(amountUsd) {
  const abs = Math.abs(amountUsd).toFixed(2);
  if (amountUsd >= 0) {
    return { text: `+$${abs}`, color: 'var(--success)' };
  }
  return { text: `-$${abs}`, color: 'var(--danger)' };
}

function ledgerActivityLabel(entry) {
  if (entry.note) return entry.note;
  return LEDGER_TYPE_LABELS[entry.type] || entry.type;
}

function syncSettingsDraftFromWallet(wallet, _presets) {
  return {
    lowBalanceThresholdUsd: wallet.lowBalanceThresholdUsd ?? 0,
    dailyCapUsd: wallet.dailyCapUsd ?? '',
    monthlyCapUsd: wallet.monthlyCapUsd ?? '',
    noDailyCap: wallet.dailyCapUsd === null,
    noMonthlyCap: wallet.monthlyCapUsd === null,
    autoRechargeEnabled: wallet.autoRecharge?.enabled ?? false,
    autoRechargeConsent: false,
  };
}

export default function Billing() {
  const { currentUser, userProfile, loadUserProfile } = useAuth();
  const { selectedWorkspaceId, workspaceReady, isOwner } = useWorkspace();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [creatingPortal, setCreatingPortal] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Wallet (SMS/voice credits) — isolated from subscription state
  const [walletData, setWalletData] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(null);
  const [topupRedirecting, setTopupRedirecting] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [topupPollStatus, setTopupPollStatus] = useState(null);
  const [exportingLedger, setExportingLedger] = useState(false);
  const [smsNumberStatus, setSmsNumberStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const autoRechargeDetailsRef = React.useRef(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Check email verification
    if (!currentUser.emailVerified) {
      navigate('/verify-email');
      return;
    }

    if (currentUser) {
      loadSubscriptionStatus();
    }
  }, [currentUser, searchParams, navigate]);

  // Auto-start checkout after subscription status loads (only when an explicit
  // checkout=true&plan=... is present, e.g. a user who chose to add a card).
  useEffect(() => {
    if (subscriptionStatus && !subscriptionStatus.subscriptionId) {
      const plan = searchParams.get('plan');
      if (plan && searchParams.get('checkout') === 'true') {
        const timer = setTimeout(() => {
          handleUpgrade(plan);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [subscriptionStatus, searchParams]);

  // Load wallet for workspace owners (skip when return-poll effect handles topup=success)
  useEffect(() => {
    if (!currentUser || !workspaceReady || !selectedWorkspaceId) return;
    if (searchParams.get('topup') === 'success') return;
    loadWallet();
    smsApi.getSmsStatus(selectedWorkspaceId).then((res) => {
      setSmsNumberStatus(res?.smsNumber?.status || null);
    }).catch(() => {});
  }, [currentUser, selectedWorkspaceId, workspaceReady]);

  useEffect(() => {
    if (!currentUser || !workspaceReady || !selectedWorkspaceId) return;
    let cancelled = false;
    usageApi.getUsage(selectedWorkspaceId)
      .then((res) => {
        if (!cancelled) setUsage(res?.success ? res : null);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser, selectedWorkspaceId, workspaceReady]);

  useEffect(() => {
    if (location.hash !== '#auto-recharge' || !autoRechargeDetailsRef.current) return;
    autoRechargeDetailsRef.current.open = true;
    autoRechargeDetailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, settingsDraft]);

  // Poll wallet after Stripe top-up return (webhook is async)
  useEffect(() => {
    if (!currentUser || !workspaceReady || !selectedWorkspaceId) return;
    if (searchParams.get('topup') !== 'success') return;

    const workspaceId = selectedWorkspaceId;
    let cancelled = false;

    async function pollTopupReturn() {
      setTopupPollStatus('processing');

      let snapshotAvailableUsd = 0;
      const knownLedgerIds = new Set();

      try {
        const initial = await walletApi.getWallet(workspaceId);
        if (cancelled) return;

        if (initial?.success) {
          applyWalletResponse(initial);
          snapshotAvailableUsd = initial.wallet?.availableUsd ?? 0;
          (initial.ledger || []).forEach((e) => knownLedgerIds.add(e.id));
        }

        for (let attempt = 0; attempt < 5; attempt++) {
          await new Promise((r) => setTimeout(r, 2000));
          if (cancelled) return;

          const res = await walletApi.getWallet(workspaceId);
          if (cancelled) return;
          if (!res?.success) continue;

          applyWalletResponse(res);
          const avail = res.wallet?.availableUsd ?? 0;
          const hasNewLedger = (res.ledger || []).some((e) => !knownLedgerIds.has(e.id));

          if (avail > snapshotAvailableUsd || hasNewLedger) {
            setTopupPollStatus(null);
            navigate('/billing', { replace: true });
            return;
          }
        }

        setTopupPollStatus('slow');
        navigate('/billing', { replace: true });
      } catch (err) {
        console.error('Error polling wallet after top-up:', err);
        if (!cancelled) {
          setTopupPollStatus('slow');
          navigate('/billing', { replace: true });
        }
      }
    }

    pollTopupReturn();
    return () => { cancelled = true; };
  }, [currentUser, selectedWorkspaceId, workspaceReady, searchParams, navigate]);

  // Fallback billing state derived from the user profile (set by AuthContext on signup).
  // Used when the status endpoint is unreachable so a fresh account defaults to its
  // real trialing state instead of an incorrect "trial ended".
  function profileFallbackStatus() {
    if (!userProfile) return null;
    return {
      subscriptionStatus: userProfile.subscriptionStatus || 'trialing',
      subscriptionPlan: userProfile.subscriptionPlan || null,
      trialActive: userProfile.trialActive ?? true,
      trialEndDate: userProfile.trialEndDate || null,
      subscriptionId: userProfile.subscriptionId || null,
      hasActiveAccess: true,
      hasPaymentMethodOnFile: false,
      userEmail: userProfile.email || currentUser?.email || null,
    };
  }

  async function loadSubscriptionStatus() {
    if (!currentUser) return;

    try {
      setLoading(true);
      // Wait for a fresh ID token before calling the (auth-required) endpoint so a
      // brand-new account doesn't hit "Unauthorized" before its token is ready.
      await currentUser.getIdToken();
      const response = await fetch(functionsUrl('/getSubscriptionStatus'), {
        headers: await getAuthHeaders(),
      });
      const data = await response.json();
      
      if (response.ok) {
        setSubscriptionStatus(data);
      } else {
        // Never strand a fresh account on an error/"trial ended" view; fall back to
        // the trialing state from the profile.
        const fallback = profileFallbackStatus();
        if (fallback) {
          setSubscriptionStatus(fallback);
        } else {
          setError(data.error || 'Failed to load subscription status');
        }
      }
    } catch (err) {
      console.error('Error loading subscription status:', err);
      const fallback = profileFallbackStatus();
      if (fallback) {
        setSubscriptionStatus(fallback);
      } else {
        setError('Failed to load subscription status');
      }
    } finally {
      setLoading(false);
    }
  }

  function applyWalletResponse(data) {
    setWalletData(data);
    if (data?.wallet) {
      setSettingsDraft(syncSettingsDraftFromWallet(data.wallet, data.topupPresetsUsd));
    }
  }

  async function loadWallet() {
    if (!currentUser || !selectedWorkspaceId) return;

    try {
      setWalletLoading(true);
      setWalletError(null);
      const data = await walletApi.getWallet(selectedWorkspaceId);
      if (data?.success) {
        applyWalletResponse(data);
      } else {
        setWalletError(data?.error || 'Failed to load wallet');
      }
    } catch (err) {
      console.error('Error loading wallet:', err);
      setWalletError(err.message || 'Failed to load wallet');
    } finally {
      setWalletLoading(false);
    }
  }

  async function handleWalletTopup(amountUsd) {
    if (!currentUser || !selectedWorkspaceId) return;

    try {
      setTopupRedirecting(true);
      setWalletError(null);
      const res = await walletApi.createTopup(selectedWorkspaceId, amountUsd, currentUser.email);
      if (res?.url) {
        window.location.href = res.url;
      } else {
        setWalletError(res?.error || 'Failed to start top-up');
        setTopupRedirecting(false);
      }
    } catch (err) {
      console.error('Error creating top-up session:', err);
      setWalletError(err.message || 'Failed to start top-up');
      setTopupRedirecting(false);
    }
  }

  function validateWalletSettings(draft) {
    if (draft.lowBalanceThresholdUsd < 0 || draft.lowBalanceThresholdUsd > 100) {
      return 'Low-balance threshold must be between $0 and $100';
    }
    if (!draft.noDailyCap) {
      const daily = Number(draft.dailyCapUsd);
      if (Number.isNaN(daily) || daily < 0 || daily > 10000) {
        return 'Daily cap must be between $0 and $10,000';
      }
    }
    if (!draft.noMonthlyCap) {
      const monthly = Number(draft.monthlyCapUsd);
      if (Number.isNaN(monthly) || monthly < 0 || monthly > 10000) {
        return 'Monthly cap must be between $0 and $10,000';
      }
    }
    if (draft.autoRechargeEnabled) {
      const alreadyArmed = walletData?.wallet?.autoRecharge?.enabled === true;
      if (!alreadyArmed && !draft.autoRechargeConsent) {
        return 'Confirm the auto-recharge authorization to turn this on';
      }
      if (!alreadyArmed && !walletData?.wallet?.autoRecharge?.hasCard) {
        return 'Add a card on file (top up once or open Customer Portal) before enabling auto-recharge';
      }
    }
    return null;
  }

  async function handleSaveWalletSettings() {
    if (!currentUser || !selectedWorkspaceId || !settingsDraft) return;

    const presets = walletData?.topupPresetsUsd || [10, 25, 50, 100];
    const validationError = validateWalletSettings(settingsDraft);
    if (validationError) {
      setSettingsError(validationError);
      return;
    }

    try {
      setSettingsSaving(true);
      setSettingsError(null);
      setSettingsSaved(false);

      const payload = {
        lowBalanceThresholdUsd: Number(settingsDraft.lowBalanceThresholdUsd),
        dailyCapUsd: settingsDraft.noDailyCap ? null : Number(settingsDraft.dailyCapUsd),
        monthlyCapUsd: settingsDraft.noMonthlyCap ? null : Number(settingsDraft.monthlyCapUsd),
        autoRecharge: {
          enabled: settingsDraft.autoRechargeEnabled,
          consentChecked: settingsDraft.autoRechargeConsent === true,
          consentText: settingsDraft.autoRechargeEnabled ? AUTO_RECHARGE_CONSENT_TEXT : undefined,
        },
      };

      const res = await walletApi.setSettings(selectedWorkspaceId, payload);
      if (res?.success && res.wallet) {
        setWalletData((prev) => ({
          ...prev,
          wallet: res.wallet,
        }));
        setSettingsDraft(syncSettingsDraftFromWallet(res.wallet, presets));
        setSettingsSaved(true);
        setTimeout(() => setSettingsSaved(false), 3000);
        await loadWallet();
      } else {
        setSettingsError(res?.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving wallet settings:', err);
      setSettingsError(err.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  }

  async function handleExportLedger() {
    if (!currentUser || !selectedWorkspaceId) return;

    try {
      setExportingLedger(true);
      setWalletError(null);
      const res = await walletApi.getLedger(selectedWorkspaceId);
      if (!res?.success || !Array.isArray(res.ledger)) {
        setWalletError(res?.error || 'Failed to export wallet history');
        return;
      }

      const escapeCsv = (value) => {
        const str = value == null ? '' : String(value);
        if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
        return str;
      };

      const rows = [
        ['date', 'type', 'note', 'amount_usd', 'running_balance'],
        ...res.ledger.map((entry) => [
          formatDate(entry.createdAt),
          entry.type,
          ledgerActivityLabel(entry),
          entry.amountUsd.toFixed(2),
          entry.balanceAfterUsd.toFixed(2),
        ]),
      ];

      const csv = rows.map((row) => row.map(escapeCsv).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `wallet-ledger-${selectedWorkspaceId.slice(0, 8)}-${stamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting wallet ledger:', err);
      setWalletError(err.message || 'Failed to export wallet history');
    } finally {
      setExportingLedger(false);
    }
  }

  async function handleCancelSubscription() {
    if (!currentUser || !subscriptionStatus?.subscriptionId || subscriptionStatus?.cancelAtPeriodEnd) {
      setError('No active subscription to cancel');
      return;
    }

    try {
      setCanceling(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(functionsUrl('/cancelSubscription'), {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          userId: currentUser.uid,
          subscriptionId: subscriptionStatus.subscriptionId,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const inTrial = subscriptionStatus?.trialActive && subscriptionStatus?.trialEndDate && getDaysRemaining(subscriptionStatus.trialEndDate) > 0;
        setSuccess(inTrial
          ? "You won't be charged. You'll keep access until your trial ends."
          : 'Subscription canceled successfully. You will continue to have access until the end of your billing period.');
        await loadSubscriptionStatus();
        if (loadUserProfile) {
          await loadUserProfile(currentUser.uid);
        }
      } else {
        setError(data.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError('Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  }

  async function handleManageBilling() {
    if (!currentUser) return;
    try {
      setCreatingPortal(true);
      setError(null);
      const returnUrl = `${window.location.origin}/billing`;
      const response = await fetch(functionsUrl('/createCustomerPortalSession'), {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ userId: currentUser.uid, returnUrl }),
      });
      let data = {};
      try {
        data = await response.json();
      } catch (_) {
        data = { message: 'Server returned an invalid response.' };
      }
      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        let msg = data.message || data.error || 'Failed to open billing portal';
        if (response.status === 500) {
          if (msg && !/customer portal|Stripe Dashboard|STRIPE_SECRET_KEY|Secret Manager/i.test(msg)) {
            msg += ' Enable Stripe Customer Portal: Stripe Dashboard → Billing → Customer portal (Activate). Also ensure STRIPE_SECRET_KEY is set in Firebase Secret Manager for this function.';
          }
        }
        setError(msg);
      }
    } catch (err) {
      console.error('Error opening portal:', err);
      setError('Connection error. If this keeps happening, enable Stripe Customer Portal in Stripe Dashboard → Billing → Customer portal.');
    } finally {
      setCreatingPortal(false);
    }
  }

  async function handleUpgrade(plan) {
    if (!currentUser || !userProfile) {
      setError('Please log in to upgrade');
      return;
    }

    try {
      setCreatingCheckout(true);
      setError(null);

      // Only give Stripe a 14-day trial when the user hasn't already had a trial that ended
      const daysLeft = getDaysRemaining(subscriptionStatus?.trialEndDate);
      const trialEnded = daysLeft !== null && daysLeft <= 0;
      const giveStripeTrial = !subscriptionStatus?.hasActiveAccess && !trialEnded;

      // createCheckoutSession requires a Firebase ID token (requireAuth) — without
      // this header every upgrade attempt fails with 401 before reaching Stripe.
      await currentUser.getIdToken();
      const response = await fetch(functionsUrl('/createCheckoutSession'), {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          userId: currentUser.uid,
          workspaceId: currentUser.uid, // subscription is pooled on the owner
          email: currentUser.email,
          plan,
          isTrial: giveStripeTrial,
        }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        const msg = data.message || data.error || 'Failed to create checkout session';
        setError(msg);
        console.error('[Billing] Checkout error', response.status, data);
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.message || 'Failed to start checkout. Check console and network tab.');
    } finally {
      setCreatingCheckout(false);
    }
  }

  function formatDate(timestamp) {
    if (timestamp == null) return 'N/A';
    let date;
    if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp.seconds != null || timestamp._seconds != null) {
      const sec = timestamp.seconds ?? timestamp._seconds;
      date = new Date(sec * 1000);
    } else {
      date = new Date(timestamp);
    }
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  function getDaysRemaining(trialEndDate) {
    if (!trialEndDate) return null;
    let end;
    if (typeof trialEndDate === 'string' || typeof trialEndDate === 'number') {
      end = new Date(trialEndDate);
    } else if (typeof trialEndDate.toDate === 'function') {
      end = trialEndDate.toDate();
    } else if (trialEndDate.seconds != null || trialEndDate._seconds != null) {
      const sec = trialEndDate.seconds ?? trialEndDate._seconds;
      end = new Date(sec * 1000);
    } else {
      end = new Date(trialEndDate);
    }
    if (Number.isNaN(end.getTime())) return null;
    const now = new Date();
    const diff = end - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  }

  function getCancelConfirmMessage() {
    const inTrial = subscriptionStatus?.trialActive && subscriptionStatus?.trialEndDate && getDaysRemaining(subscriptionStatus.trialEndDate) > 0;
    const trialEndStr = subscriptionStatus?.trialEndDate ? formatDate(subscriptionStatus.trialEndDate) : 'your trial end date';
    return inTrial
      ? `Cancel before your trial ends? You won't be charged. You'll keep access until ${trialEndStr}.`
      : 'Cancel your subscription? You will keep access until the end of your billing period.';
  }

  // Submission limit from tier (matches backend tiers.ts); custom tier can use profile override
  function getSubmissionLimit() {
    const tier = resolveEffectiveTier(userProfile);
    return getSubmissionLimitForTier(tier, userProfile?.submissionLimit);
  }

  const effectiveTier = resolveEffectiveTier(userProfile);
  const overageRate = LEAD_OVERAGE_USD_BY_TIER[effectiveTier];
  const submissionsUsed = usage?.poolUsed ?? userProfile?.submissionsUsed ?? 0;
  const submissionLimit = usage?.submissionLimit ?? getSubmissionLimit();
  const includedLeadsUsed = overageRate != null ? Math.min(submissionsUsed, submissionLimit) : submissionsUsed;
  const overageLeads = overageRate != null ? Math.max(0, submissionsUsed - submissionLimit) : 0;
  const workspacesOwned = usage?.workspacesOwned;
  const workspaceLimitLabel = usage?.workspaceLimit === -1 ? 'unlimited' : usage?.workspaceLimit;

  const daysRemaining = subscriptionStatus?.trialEndDate
    ? getDaysRemaining(subscriptionStatus.trialEndDate)
    : null;
  const trialLapsed = daysRemaining !== null && daysRemaining <= 0;

  const billingState = subscriptionStatus
    ? deriveBillingState({
        rawSubscriptionStatus: subscriptionStatus.rawSubscriptionStatus ?? null,
        subscriptionId: subscriptionStatus.subscriptionId || null,
        cancelAtPeriodEnd: subscriptionStatus.cancelAtPeriodEnd === true,
        currentPeriodEnd: subscriptionStatus.currentPeriodEnd || subscriptionStatus.cancelEffectiveAt || null,
        effectiveTier,
        canceledAt: subscriptionStatus.canceledAt || null,
        resolvedSubscriptionStatus: subscriptionStatus.subscriptionStatus || null,
      })
    : BILLING_STATES.NEVER_SUBSCRIBED;

  const periodEndFormatted = subscriptionStatus?.currentPeriodEnd
    ? formatDate(subscriptionStatus.currentPeriodEnd)
    : subscriptionStatus?.cancelEffectiveAt
      ? formatDate(subscriptionStatus.cancelEffectiveAt)
      : null;
  const canceledAtFormatted = subscriptionStatus?.canceledAt
    ? formatDate(subscriptionStatus.canceledAt)
    : null;

  const cancelProps = cancelControlProps(billingState, {
    canceledAtFormatted,
    periodEndFormatted,
  });
  const bannerFlags = statusBannerFlags(billingState, trialLapsed);

  const hasActiveSub =
    billingState === BILLING_STATES.ACTIVE ||
    billingState === BILLING_STATES.CANCELED_IN_PERIOD;

  const rawPlan =
    subscriptionStatus?.subscriptionPlan ||
    (hasActiveSub ? userProfile?.tier : null);
  const effectivePlan = rawPlan?.toLowerCase?.() || null;

  function planButtonLabel(targetPlan) {
    return billingPlanButtonLabel(
      billingState,
      targetPlan,
      effectivePlan,
      creatingCheckout,
      periodEndFormatted
    );
  }

  const isUserAdmin = isAdmin(currentUser, userProfile);
  const isWorkspaceOwner = isOwner || isUserAdmin;
  const canManageSubscription = isWorkspaceOwner;

  const pagePad = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';

  const cancelPillStyle = {
    padding: '0.5rem 1rem',
    background: 'var(--danger)',
    color: '#fff',
    border: 'none',
    borderRadius: '999px',
    cursor: canceling ? 'not-allowed' : 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    fontFamily: 'inherit',
  };

  if (loading) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
        <div className="today-page billing-page" style={{ padding: isMobile ? '1rem 1rem 2rem' : '24px 32px 48px' }}>
          <h1 className="today-h1" style={{ marginBottom: '1.5rem' }}>Billing</h1>
          <BillingPlanSkeleton />
        </div>
      </div>
    );
  }

  const showTrialExpiredRedirect = searchParams.get('trial_expired') === '1';

  const billingAccountEmail = subscriptionStatus?.userEmail || currentUser?.email || '';
  const hasPaymentMethod = subscriptionStatus?.hasPaymentMethodOnFile === true || !!subscriptionStatus?.subscriptionId;

  function renderCancelControl() {
    if (cancelProps.enabled) {
      return (
        <>
          <InlineConfirm
            message={getCancelConfirmMessage()}
            onConfirm={handleCancelSubscription}
            disabled={canceling}
            loading={canceling}
            buttonLabel={canceling ? 'Canceling...' : cancelProps.buttonLabel}
            loadingLabel="Canceling..."
            buttonStyle={cancelPillStyle}
          />
          {cancelProps.helperText && (
            <p className="billing-callout-text" style={{ marginTop: '0.75rem', marginBottom: 0, fontSize: '0.85rem' }}>
              {cancelProps.helperText}
            </p>
          )}
        </>
      );
    }

    return (
      <p className="billing-callout-text" style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-6)' }}>
        {cancelProps.buttonLabel}
      </p>
    );
  }

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <div className="today-page billing-page" style={{ padding: pagePad }}>
        <div className="today-header">
          <div className="today-header-left" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
            <h1 className="today-h1">Billing &amp; Subscription</h1>
            {billingAccountEmail && (
              <p className="billing-account-email">
                <strong>Billing account:</strong> {billingAccountEmail}
              </p>
            )}
          </div>
        </div>

        {showTrialExpiredRedirect && canManageSubscription && (
          <div className="billing-banner-row">
            <div className="fb-callout warn" style={{ marginBottom: 0 }}>
              <strong>Trial expired.</strong> Add a payment method below to restore access to your forms, leads, and all features.
            </div>
            <a href="#plans" className="inbox-pill-btn-primary" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Choose a plan
            </a>
          </div>
        )}

        {billingState === BILLING_STATES.PAST_DUE && (
          <div className="fb-callout warn billing-section-panel">
            <h3 className="billing-callout-title">Payment issue: update your card to avoid restriction</h3>
            <p className="billing-callout-text" style={{ marginBottom: 0 }}>
              We couldn&apos;t process your payment. Update your payment method below. You have 14 days before access is restricted.
            </p>
          </div>
        )}

        {bannerFlags.showCanceledEnded && (
          <div className="fb-callout warn billing-section-panel">
            <h3 className="billing-callout-title">{canceledEndedBannerText(canceledAtFormatted)}</h3>
          </div>
        )}

        {bannerFlags.showTrialEnded && (
          <div className="fb-callout danger billing-section-panel">
            <h3 className="billing-callout-title">Trial ended. Add payment to continue</h3>
            <p className="billing-callout-text">
              Your trial has ended. You can still view Leads, but Form Builder and other features require a plan. Add a payment method below to restore full access.
            </p>
            <p className="billing-callout-text" style={{ marginBottom: 0 }}>
              Choose a plan below and click Upgrade to add your card.
            </p>
          </div>
        )}

        {error && (
          <div className="today-load-error billing-section-panel">{error}</div>
        )}

        {success && (
          <div className="fb-callout success billing-section-panel">{success}</div>
        )}

        <div className="today-panel billing-section-panel">
          <div className="today-panel-header billing-panel-toolbar">
            <span className="today-panel-title">Current Plan</span>
            {canManageSubscription && subscriptionStatus?.subscriptionId && (
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={handleManageBilling}
                disabled={creatingPortal}
                style={{ flexShrink: 0 }}
              >
                {creatingPortal ? 'Opening...' : 'Manage billing & subscription'}
              </button>
            )}
          </div>
          <div className="today-panel-body">

        {!canManageSubscription && (
          <p className="billing-callout-text" style={{ marginTop: 0, marginBottom: '1rem' }}>
            Billing is managed by the workspace owner.
          </p>
        )}

        {(billingState === BILLING_STATES.ACTIVE || billingState === BILLING_STATES.CANCELED_IN_PERIOD) ? (
          <div className={`fb-callout ${billingState === BILLING_STATES.CANCELED_IN_PERIOD ? 'warn' : 'success'} billing-status-block`}>
            <div className="billing-status-row">
              <div className="billing-status-main">
                <h3 className="billing-callout-title">
                  {billingState === BILLING_STATES.CANCELED_IN_PERIOD ? 'Subscription canceling' : 'Active Subscription'}
                </h3>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div className="billing-plan-label">Plan</div>
                  <div className="billing-plan-name">
                    {effectivePlan
                      ? effectivePlan.charAt(0).toUpperCase() + effectivePlan.slice(1)
                      : 'Unknown'}
                  </div>
                </div>
                <div className="billing-callout-text" style={{ marginBottom: '0.75rem' }}>
                  <strong>Payment method:</strong> {hasPaymentMethod ? 'On file' : 'None on file'}
                </div>
                {subscriptionStatus?.currentPeriodEnd && (
                  <div className="billing-callout-text">
                    {subscriptionStatus.cancelAtPeriodEnd
                      ? `Access until: ${formatDate(new Date(subscriptionStatus.currentPeriodEnd))}`
                      : `Renews: ${formatDate(new Date(subscriptionStatus.currentPeriodEnd))}`}
                  </div>
                )}
                {userProfile && (
                  <div className="billing-usage-section">
                    <div className="billing-plan-label">Usage This Month</div>
                    <div className="billing-plan-name" style={{ fontSize: '1rem' }}>
                      <strong>{submissionsUsed}</strong> / {submissionLimit} leads
                      {overageRate != null && overageLeads > 0 && (
                        <span style={{ display: 'block', fontSize: '0.85rem', marginTop: '0.25rem', color: 'var(--text-6)' }}>
                          {includedLeadsUsed} included · {overageLeads} overage @ ${Number(overageRate).toFixed(2)}/lead
                        </span>
                      )}
                    </div>
                    <div className="billing-usage-bar">
                      <div
                        className="billing-usage-bar-fill"
                        style={{ width: `${Math.min(100, (submissionsUsed / Math.max(submissionLimit, 1)) * 100)}%` }}
                      />
                    </div>
                    {workspacesOwned != null && workspaceLimitLabel != null && (
                      <div className="billing-callout-text" style={{ marginTop: '0.5rem' }}>
                        Client workspaces: {workspacesOwned} of {workspaceLimitLabel}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="billing-status-aside">
                <div className="billing-status-label">Status</div>
                <span className={`inbox-chip ${billingState === BILLING_STATES.CANCELED_IN_PERIOD ? 'warn' : 'accent-soft'}`} style={{ marginBottom: '1rem', display: 'inline-block' }}>
                  {billingState === BILLING_STATES.CANCELED_IN_PERIOD ? 'Canceling' : 'Active'}
                </span>
              </div>
            </div>
          </div>
        ) : billingState === BILLING_STATES.PAST_DUE ? (
          <div className="fb-callout warn billing-status-block">
            <div className="billing-status-row">
              <div className="billing-status-main">
                <h3 className="billing-callout-title">Payment issue</h3>
                <p className="billing-callout-text">
                  We couldn&apos;t process your payment. This often happens when a card has expired.
                </p>
                <p className="billing-callout-text">
                  Update your payment method to avoid any interruption. You have 14 days from the first failure before access is restricted.
                </p>
                {canManageSubscription && (subscriptionStatus?.subscriptionId ? (
                  <button
                    type="button"
                    className="inbox-pill-btn-primary"
                    onClick={handleManageBilling}
                    disabled={creatingPortal}
                    style={{ marginTop: '0.75rem' }}
                  >
                    {creatingPortal ? 'Opening...' : 'Update payment method'}
                  </button>
                ) : (
                  <p className="billing-callout-text" style={{ marginTop: '0.75rem' }}>
                    <a href="#plans" className="billing-inline-link">Choose a plan below</a> to add a payment method.
                  </p>
                ))}
              </div>
              <div className="billing-status-aside">
                <div className="billing-status-label">Status</div>
                <span className="inbox-chip warn">Past due</span>
              </div>
            </div>
          </div>
        ) : billingState === BILLING_STATES.NEVER_SUBSCRIBED && subscriptionStatus?.trialActive && !trialLapsed ? (
          <div className="fb-callout warn billing-status-block">
            <div className="billing-status-row">
              <div className="billing-status-main">
                <h3 className="billing-callout-title">Free Trial Active</h3>
                <>
                    <p className="billing-callout-text">
                      <strong>{daysRemaining} days</strong> remaining
                    </p>
                    <p className="billing-callout-text">
                      Trial ends: {formatDate(subscriptionStatus.trialEndDate)}
                    </p>
                    <p className="billing-callout-text">
                      <strong>Current plan:</strong> {subscriptionStatus?.subscriptionPlan
                        ? subscriptionStatus.subscriptionPlan.charAt(0).toUpperCase() + subscriptionStatus.subscriptionPlan.slice(1)
                        : 'None selected, choose below'}
                    </p>
                    {hasPaymentMethod ? (
                      <div className="fb-callout success" style={{ marginTop: '0.75rem' }}>
                        <strong>Payment method on file</strong>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                          {subscriptionStatus?.subscriptionPlan
                            ? `${subscriptionStatus.subscriptionPlan.charAt(0).toUpperCase() + subscriptionStatus.subscriptionPlan.slice(1)} plan selected. `
                            : subscriptionStatus?.subscriptionId
                              ? 'You will be charged when your trial ends. '
                              : 'Card on file. Choose a plan below to subscribe before your trial ends. '}
                          {subscriptionStatus?.subscriptionId && subscriptionStatus?.trialEndDate && (
                            <>You will be charged when your trial ends on {formatDate(subscriptionStatus.trialEndDate)}.<br /></>
                          )}
                          {canManageSubscription && (
                          <button type="button" onClick={handleManageBilling} disabled={creatingPortal} className="fb-link-inline" style={{ color: 'var(--accent-ink)', marginTop: '0.25rem' }}>
                            {creatingPortal ? 'Opening...' : 'Update payment method or change plan'}
                          </button>
                          )}
                        </p>
                      </div>
                    ) : (
                      <div className="fb-callout warn" style={{ marginTop: '0.75rem' }}>
                        <strong>No payment method on file</strong>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                          Add a payment method before your trial ends to keep access. Choose a plan below or open the billing portal to add a card.
                        </p>
                      </div>
                    )}
                  </>
              </div>
              <div className="billing-status-aside">
                <div className="billing-status-label">Status</div>
                <span className="inbox-chip warn">Trial</span>
              </div>
            </div>
            {canManageSubscription && (
            <p className="billing-footer-note">
              Upgrade now to continue using gocta.ai after your trial ends.
            </p>
            )}
          </div>
        ) : billingState === BILLING_STATES.ASSIGNED_NO_SUB ? (
          <div className="fb-callout warn billing-status-block">
            <div className="billing-status-row">
              <div className="billing-status-main">
                <h3 className="billing-callout-title">
                  {userProfile.tier.charAt(0).toUpperCase() + userProfile.tier.slice(1)} Plan
                </h3>
                <p className="billing-callout-text">
                  Plan assigned to your account — no payment subscription on file.
                </p>
                {userProfile && (
                  <div className="billing-usage-section">
                    <div className="billing-plan-label">Usage This Month</div>
                    <div className="billing-plan-name" style={{ fontSize: '1rem' }}>
                      <strong>{submissionsUsed}</strong> / {submissionLimit} leads
                      {overageRate != null && overageLeads > 0 && (
                        <span style={{ display: 'block', fontSize: '0.85rem', marginTop: '0.25rem', color: 'var(--text-6)' }}>
                          {includedLeadsUsed} included · {overageLeads} overage @ ${Number(overageRate).toFixed(2)}/lead
                        </span>
                      )}
                    </div>
                    {workspacesOwned != null && workspaceLimitLabel != null && (
                      <div className="billing-callout-text" style={{ marginTop: '0.5rem' }}>
                        Client workspaces: {workspacesOwned} of {workspaceLimitLabel}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="billing-status-aside">
                <div className="billing-status-label">Status</div>
                <span className="inbox-chip warn">Assigned</span>
              </div>
            </div>
          </div>
        ) : billingState === BILLING_STATES.CANCELED_ENDED ? (
          <div className="fb-callout warn billing-status-block">
            <div className="billing-status-row">
              <div className="billing-status-main">
                <h3 className="billing-callout-title">Subscription canceled</h3>
                <p className="billing-callout-text">
                  {canceledEndedBannerText(canceledAtFormatted)}
                </p>
                {userProfile && (
                  <div className="billing-usage-section">
                    <div className="billing-plan-label">Usage This Month</div>
                    <div className="billing-plan-name" style={{ fontSize: '1rem' }}>
                      <strong>{submissionsUsed}</strong> / {submissionLimit} leads
                      {overageRate != null && overageLeads > 0 && (
                        <span style={{ display: 'block', fontSize: '0.85rem', marginTop: '0.25rem', color: 'var(--text-6)' }}>
                          {includedLeadsUsed} included · {overageLeads} overage @ ${Number(overageRate).toFixed(2)}/lead
                        </span>
                      )}
                    </div>
                    {workspacesOwned != null && workspaceLimitLabel != null && (
                      <div className="billing-callout-text" style={{ marginTop: '0.5rem' }}>
                        Client workspaces: {workspacesOwned} of {workspaceLimitLabel}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="billing-status-aside">
                <div className="billing-status-label">Status</div>
                <span className="inbox-chip danger">Canceled</span>
              </div>
            </div>
          </div>
        ) : billingState === BILLING_STATES.NEVER_SUBSCRIBED ? (
          <div className="fb-callout warn billing-status-block">
            <h3 className="billing-callout-title">No active subscription</h3>
            <p className="billing-callout-text" style={{ marginBottom: 0 }}>
              {trialLapsed
                ? 'Choose a plan below to restore access to your forms and leads.'
                : 'Choose a plan below to get started.'}
            </p>
          </div>
        ) : (
          <div className="fb-callout warn billing-status-block">
            <h3 className="billing-callout-title">Billing status</h3>
            <p className="billing-callout-text" style={{ marginBottom: 0 }}>
              Choose a plan below or manage your subscription.
            </p>
          </div>
        )}

        {canManageSubscription && (
        <div className="billing-cancel-row" style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border)' }}>
          <div className="billing-plan-label" style={{ marginBottom: '0.5rem' }}>Cancel subscription</div>
          {renderCancelControl()}
        </div>
        )}
          </div>
        </div>

        {/* Prepaid wallet for SMS, voice, and Starter lead overages */}
        {currentUser && (
        <div className="today-panel billing-section-panel">
          <div className="today-panel-header billing-panel-toolbar">
            <span className="today-panel-title">Credits</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
              {isWorkspaceOwner && walletData?.wallet && (
                <>
                  <button
                    type="button"
                    className="inbox-pill-btn"
                    onClick={handleExportLedger}
                    disabled={exportingLedger || walletLoading}
                    style={{ flexShrink: 0 }}
                  >
                    {exportingLedger ? 'Exporting...' : 'Export history (CSV)'}
                  </button>
                  <button
                    type="button"
                    className="fb-link-inline"
                    onClick={handleManageBilling}
                    disabled={creatingPortal}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: creatingPortal ? 'not-allowed' : 'pointer' }}
                  >
                    {creatingPortal ? 'Opening...' : 'Invoices & receipts'}
                  </button>
                </>
              )}
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={loadWallet}
                disabled={walletLoading}
                style={{ flexShrink: 0 }}
              >
                {walletLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
          <div className="today-panel-body">
            {topupPollStatus === 'processing' && (
              <div className="fb-callout info" style={{ marginBottom: '1rem' }}>
                Top-up processing...
              </div>
            )}
            {topupPollStatus === 'slow' && (
              <div className="fb-callout warn" style={{ marginBottom: '1rem' }}>
                This can take a moment. Hit Refresh to check your updated balance.
              </div>
            )}

            {walletError && (
              <div className="today-load-error" style={{ marginBottom: '1rem' }}>{walletError}</div>
            )}

            {walletLoading && !walletData ? (
              <p className="proof-row-muted">Loading credits...</p>
            ) : walletData?.wallet ? (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <div className="billing-plan-name" style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>
                    ${walletData.wallet.availableUsd.toFixed(2)} available
                  </div>
                  {walletData.wallet.reservedUsd > 0 && (
                    <p className="proof-row-muted" style={{ margin: 0 }}>
                      ${walletData.wallet.reservedUsd.toFixed(2)} in flight
                    </p>
                  )}
                  {walletData.wallet.balanceUsd < walletData.wallet.lowBalanceThresholdUsd && (
                    <span className="inbox-chip warn" style={{ marginTop: '0.5rem', display: 'inline-block' }}>
                      Low balance
                    </span>
                  )}
                </div>

                <div className="fb-callout info" style={{ marginBottom: '1.25rem' }}>
                  Credits power text replies, missed-call text-back, and call forwarding minutes. Approx{' '}
                  {walletData.pricingHints?.approxSmsSegments ?? 0} texts or{' '}
                  {walletData.pricingHints?.approxVoiceMinutes ?? 0} voice minutes at your current balance.
                  {' '}
                  <Link to="/text-messaging" className="today-link-accent">Set up text messaging</Link>
                </div>

                {isOwner && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <div className="billing-plan-label" style={{ marginBottom: '0.5rem' }}>Add credits</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {(walletData.topupPresetsUsd || [10, 25, 50, 100]).map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        className="inbox-pill-btn-primary"
                        onClick={() => handleWalletTopup(amount)}
                        disabled={topupRedirecting}
                      >
                        {topupRedirecting ? 'Redirecting...' : `$${amount}`}
                      </button>
                    ))}
                  </div>
                </div>
                )}

                <div style={{ marginBottom: '1.25rem' }}>
                  <div className="billing-plan-label" style={{ marginBottom: '0.5rem' }}>Recent activity</div>
                  {(walletData.ledger || []).length === 0 ? (
                    <p className="proof-row-muted" style={{ margin: 0 }}>No activity yet.</p>
                  ) : (
                    <div>
                      {(walletData.ledger || []).slice(0, 8).map((entry) => {
                        const amt = formatLedgerAmount(entry.amountUsd);
                        return (
                          <div key={entry.id} className="proof-row">
                            <div className="proof-row-top">
                              <div className="proof-row-meta">
                                <span className="proof-row-date">{formatDate(entry.createdAt)}</span>
                                <span className="proof-row-lead">
                                  {ledgerActivityLabel(entry)}
                                </span>
                              </div>
                              <div className="proof-row-badges">
                                <span style={{ color: amt.color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                  {amt.text}
                                </span>
                                <span className="proof-row-muted">
                                  Bal ${entry.balanceAfterUsd.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {isOwner && settingsDraft && (
                  <details
                    id="auto-recharge"
                    ref={autoRechargeDetailsRef}
                    className="quarantine-data-details"
                  >
                    <summary style={{ cursor: 'pointer', fontWeight: 500, marginBottom: '0.75rem' }}>
                      Settings
                    </summary>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <label className="quarantine-field">
                        <span className="quarantine-field-label">Low-balance alert threshold ($0-$100)</span>
                        <input
                          type="number"
                          className="fb-input"
                          min={0}
                          max={100}
                          step={1}
                          value={settingsDraft.lowBalanceThresholdUsd}
                          onChange={(e) => setSettingsDraft((d) => ({
                            ...d,
                            lowBalanceThresholdUsd: Number(e.target.value),
                          }))}
                        />
                      </label>

                      <label className="quarantine-field">
                        <span className="quarantine-field-label">Daily spending cap</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={settingsDraft.noDailyCap}
                            onChange={(e) => setSettingsDraft((d) => ({
                              ...d,
                              noDailyCap: e.target.checked,
                            }))}
                          />
                          <span className="proof-row-muted">No cap</span>
                          {!settingsDraft.noDailyCap && (
                            <input
                              type="number"
                              className="fb-input"
                              min={0}
                              max={10000}
                              step={1}
                              value={settingsDraft.dailyCapUsd}
                              onChange={(e) => setSettingsDraft((d) => ({
                                ...d,
                                dailyCapUsd: e.target.value,
                              }))}
                              style={{ maxWidth: '8rem' }}
                            />
                          )}
                        </div>
                      </label>

                      <label className="quarantine-field">
                        <span className="quarantine-field-label">Monthly spending cap</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={settingsDraft.noMonthlyCap}
                            onChange={(e) => setSettingsDraft((d) => ({
                              ...d,
                              noMonthlyCap: e.target.checked,
                            }))}
                          />
                          <span className="proof-row-muted">No cap</span>
                          {!settingsDraft.noMonthlyCap && (
                            <input
                              type="number"
                              className="fb-input"
                              min={0}
                              max={10000}
                              step={1}
                              value={settingsDraft.monthlyCapUsd}
                              onChange={(e) => setSettingsDraft((d) => ({
                                ...d,
                                monthlyCapUsd: e.target.value,
                              }))}
                              style={{ maxWidth: '8rem' }}
                            />
                          )}
                        </div>
                      </label>

                      <div className="quarantine-field">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={settingsDraft.autoRechargeEnabled}
                            onChange={(e) => setSettingsDraft((d) => ({
                              ...d,
                              autoRechargeEnabled: e.target.checked,
                              autoRechargeConsent: e.target.checked ? d.autoRechargeConsent : false,
                            }))}
                          />
                          <span className="quarantine-field-label" style={{ margin: 0 }}>
                            {AUTO_RECHARGE_TOGGLE_LABEL}
                          </span>
                        </label>
                        {walletData.wallet?.autoRecharge?.enabled ? (
                          <p className="proof-row-muted" style={{ margin: '0.25rem 0 0' }}>
                            {formatAutoRechargeAuthorizedLine({
                              consentedAt: walletData.wallet.autoRecharge.consentedAt,
                              card: walletData.wallet.autoRecharge.card,
                            })}
                          </p>
                        ) : (
                          <p className="proof-row-muted" style={{ margin: '0.25rem 0 0' }}>
                            {walletData.wallet?.autoRecharge?.hasCard && walletData.wallet.autoRecharge.card
                              ? `Card on file: ${walletData.wallet.autoRecharge.card.brand} •••• ${walletData.wallet.autoRecharge.card.last4}`
                              : 'No card on file — top up once or open Customer Portal to save a card.'}
                          </p>
                        )}
                        {walletData.wallet?.autoRecharge?.needsReconsent && (
                          <p className="proof-row-muted" style={{ margin: '0.25rem 0 0' }}>
                            Re-enable to confirm
                          </p>
                        )}
                        {walletData.wallet?.autoRecharge?.pausedReason && (
                          <p className="proof-row-muted" style={{ margin: '0.25rem 0 0' }}>
                            Auto-recharge paused: {walletData.wallet.autoRecharge.pausedReason}
                          </p>
                        )}
                        {settingsDraft.autoRechargeEnabled && walletData.wallet?.autoRecharge?.enabled !== true && (
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={settingsDraft.autoRechargeConsent === true}
                              onChange={(e) => setSettingsDraft((d) => ({
                                ...d,
                                autoRechargeConsent: e.target.checked,
                              }))}
                              style={{ marginTop: '0.2rem' }}
                            />
                            <span className="proof-row-muted">
                              {walletData.wallet?.autoRecharge?.consentText || AUTO_RECHARGE_CONSENT_TEXT}
                            </span>
                          </label>
                        )}
                      </div>

                      {settingsError && (
                        <div className="today-load-error">{settingsError}</div>
                      )}
                      {settingsSaved && (
                        <div className="fb-callout success">Saved</div>
                      )}

                      <button
                        type="button"
                        className="inbox-pill-btn-primary"
                        onClick={handleSaveWalletSettings}
                        disabled={settingsSaving}
                        style={{ alignSelf: 'flex-start' }}
                      >
                        {settingsSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </details>
                )}
              </>
            ) : null}
          </div>
        </div>
        )}

        <div className="today-panel billing-section-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">
              {smsNumberStatus === 'active' ? 'Text messaging: active · Manage' : 'Text messaging add-on'}
            </span>
          </div>
          <div className="today-panel-body">
            <p className="billing-plans-desc" style={{ marginTop: 0 }}>
              SMS setup: <strong>$49 one-time</strong> + <strong>$19.99/mo</strong> platform fee (wallet).
              Outbound texts billed per segment from your wallet above.
            </p>
            <Link to="/text-messaging" className="inbox-pill-btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
              {smsNumberStatus === 'active' ? 'Text messaging: active · Manage' : 'Set up text messaging'}
            </Link>
          </div>
        </div>

        {canManageSubscription && (
        <div id="plans" className="today-panel billing-section-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Choose Your Plan</span>
          </div>
          <div className="today-panel-body">
            <p className="billing-plans-desc">
              All plans include unlimited spam filtering and AI-powered form responses.
            </p>

            <div className="billing-plan-grid">
              <div className={`billing-plan-card${effectivePlan === 'starter' ? ' billing-plan-card--current' : ''}`}>
                <h3 style={{ marginTop: 0, color: 'var(--ink)' }}>Starter</h3>
                <div className="billing-plan-price">
                  $89<span className="billing-plan-price-period">/month</span>
                </div>
                <ul className="billing-plan-features">
                  <li>40 verified leads/month</li>
                  <li>$2.50/lead overage (wallet)</li>
                  <li>1 form</li>
                  <li>1 client workspace</li>
                  <li>AI auto-replies + spam filtering</li>
                  <li>SMS add-on available</li>
                </ul>
                <button
                  type="button"
                  className={effectivePlan === 'starter' ? 'inbox-pill-btn billing-plan-btn' : 'inbox-pill-btn-primary billing-plan-btn'}
                  onClick={() => handleUpgrade('starter')}
                  disabled={isPlanButtonDisabled(billingState, 'starter', effectivePlan, creatingCheckout)}
                >
                  {planButtonLabel('starter')}
                </button>
              </div>

              <div className={`billing-plan-card billing-plan-card--featured${effectivePlan === 'growth' ? ' billing-plan-card--current' : ''}`}>
                {effectivePlan !== 'growth' && (
                  <span className="inbox-chip accent billing-plan-popular">Most popular</span>
                )}
                <h3 style={{ marginTop: 0, color: 'var(--ink)' }}>Growth</h3>
                <div className="billing-plan-price">
                  $200<span className="billing-plan-price-period">/month</span>
                </div>
                <ul className="billing-plan-features">
                  <li>100 verified leads/month</li>
                  <li>$2.00/lead overage (wallet)</li>
                  <li>Unlimited spam filtering</li>
                  <li>AI auto-replies</li>
                  <li>3 client workspaces</li>
                  <li>Custom domains</li>
                </ul>
                <button
                  type="button"
                  className={effectivePlan === 'growth' ? 'inbox-pill-btn billing-plan-btn' : 'inbox-pill-btn-primary billing-plan-btn'}
                  onClick={() => handleUpgrade('growth')}
                  disabled={isPlanButtonDisabled(billingState, 'growth', effectivePlan, creatingCheckout)}
                >
                  {planButtonLabel('growth')}
                </button>
              </div>

              <div className={`billing-plan-card${effectivePlan === 'professional' ? ' billing-plan-card--current' : ''}`}>
                <h3 style={{ marginTop: 0, color: 'var(--ink)' }}>Professional</h3>
                <div className="billing-plan-price">
                  $300<span className="billing-plan-price-period">/month</span>
                </div>
                <ul className="billing-plan-features">
                  <li>250 verified leads/month</li>
                  <li>$1.50/lead overage (wallet)</li>
                  <li>Unlimited spam filtering</li>
                  <li>AI auto-replies</li>
                  <li>10 client workspaces</li>
                  <li>Custom domains</li>
                  <li>Priority support</li>
                </ul>
                <button
                  type="button"
                  className={effectivePlan === 'professional' ? 'inbox-pill-btn billing-plan-btn' : 'inbox-pill-btn-primary billing-plan-btn'}
                  onClick={() => handleUpgrade('professional')}
                  disabled={isPlanButtonDisabled(billingState, 'professional', effectivePlan, creatingCheckout)}
                >
                  {planButtonLabel('professional')}
                </button>
              </div>

              <div className={`billing-plan-card${effectivePlan === 'enterprise' ? ' billing-plan-card--current' : ''}`}>
                <h3 style={{ marginTop: 0, color: 'var(--ink)' }}>Enterprise</h3>
                <div className="billing-plan-price">
                  $500<span className="billing-plan-price-period">/month</span>
                </div>
                <ul className="billing-plan-features">
                  <li>500 verified leads/month</li>
                  <li>$1.00/lead overage (wallet)</li>
                  <li>Unlimited spam filtering</li>
                  <li>AI auto-replies</li>
                  <li>Unlimited client workspaces</li>
                  <li>Custom domains</li>
                  <li>Priority support</li>
                  <li>Custom integrations</li>
                </ul>
                <button
                  type="button"
                  className={effectivePlan === 'enterprise' ? 'inbox-pill-btn billing-plan-btn' : 'inbox-pill-btn-primary billing-plan-btn'}
                  onClick={() => handleUpgrade('enterprise')}
                  disabled={isPlanButtonDisabled(billingState, 'enterprise', effectivePlan, creatingCheckout)}
                >
                  {planButtonLabel('enterprise')}
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="today-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">How It Works</span>
          </div>
          <div className="today-panel-body">
            <ul className="billing-info-list">
              <li>You only pay for <strong>verified leads</strong> that pass our spam filter</li>
              <li>Spam submissions are <strong>completely free</strong> and unlimited</li>
              <li>All plans include a <strong>14-day free trial</strong> (no credit card required)</li>
              <li>Cancel anytime - no long-term contracts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
